import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../utils/apiFetch';
import '../styles/GuestList.css';
import type { Guest, SeatToGuestMap } from '../types';
import { BOOKING_CODE_MAX_LENGTH, DEFAULT_MAX_ADDITIONAL_GUESTS, MAX_GUESTS, MIN_GUESTS } from '../config';

interface GuestListProps {
  seatToGuestMap: SeatToGuestMap;
  onAssignSeat?: (guests: Guest[]) => void;
  minGuestCodes?: number;
  maxGuestCodes?: number;
  onGuestCodesUpdate?: (guests: Guest[]) => void;
  guestCodes: Guest[];
  onAssignmentsConfirmed?: (table: string | null) => void;
  /** True when the coordinator is extending an existing reservation. */
  extensionMode?: boolean;
  /** Max number of additional (non-locked) guests permitted in extension mode. */
  maxAdditionalGuests?: number;
  /** True when the current table is full and the user must pick a new one. */
  tableChangeRequired?: boolean;
  /** True when no table can accommodate all guests — user must contact staff. */
  noTablesAvailable?: boolean;
}

const GuestList: React.FC<GuestListProps> = ({
  seatToGuestMap,
  onAssignSeat,
  minGuestCodes = MIN_GUESTS,
  maxGuestCodes = MAX_GUESTS,
  onGuestCodesUpdate,
  guestCodes,
  extensionMode = false,
  maxAdditionalGuests = DEFAULT_MAX_ADDITIONAL_GUESTS,
  tableChangeRequired = false,
  noTablesAvailable = false,
}) => {
  const { t } = useTranslation();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  // Keep the latest guestCodes prop in a ref so async validation callbacks
  // (handleInputBlur) always build from current state, not a stale closure.
  const guestCodesRef = useRef<Guest[]>(guestCodes);
  useEffect(() => { guestCodesRef.current = guestCodes; }, [guestCodes]);
  const [assignmentsConfirmed, setAssignmentsConfirmed] = useState<boolean>(false);
  // Per-row error message shown beneath an invalid input (e.g. "unknown code",
  // "duplicate"). Indexed by guest-row index. Cleared when the user re-edits.
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const setRowError = (idx: number, msg: string | null) => {
    setRowErrors(prev => {
      const next = { ...prev };
      if (msg) next[idx] = msg; else delete next[idx];
      return next;
    });
  };

  // Helper to fill guestCodes to maxGuestCodes length
  const filledGuestCodes = guestCodes.concat(
    Array(Math.max(0, maxGuestCodes - guestCodes.length)).fill({ guestCode: '', guestGuid: '', seatGuid: '', valid: null })
  );

  const canAssignSeat = filledGuestCodes.filter((entry) => entry.guestGuid && entry.guestGuid.trim() !== '').length >= minGuestCodes;

  const handleGuestCodeChange = (index: number, value: string) => {
    // Only allow uppercase letters and digits, max length per spec
    const filteredValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, BOOKING_CODE_MAX_LENGTH);
    // Always update the full array, not filledGuestCodes (which is derived)
    const updated = guestCodes.concat(
      Array(Math.max(0, maxGuestCodes - guestCodes.length)).fill({ guestCode: '', guestGuid: '', seatGuid: '', valid: null })
    );
    updated[index] = { ...updated[index], guestCode: filteredValue, valid: null };
    setRowError(index, null);
    onGuestCodesUpdate?.(updated);
  };

  const handleInputBlur = async (index: number) => {
    // Always use the up-to-date guestCodes array for validation
    const currentCodes = guestCodes.concat(
      Array(Math.max(0, maxGuestCodes - guestCodes.length)).fill({ guestCode: '', guestGuid: '', seatGuid: '', valid: null })
    );
    const code = currentCodes[index].guestCode.trim();
    const isDuplicate = currentCodes.some(
      (entry, idx) =>
        idx !== index &&
        code !== '' &&
        (
          entry.guestCode.trim().toUpperCase() === code.toUpperCase() ||
          (entry.locked && (entry.displayName ?? '').trim().toUpperCase() === code.toUpperCase())
        )
    );
    if (isDuplicate) {
      const updated = [...currentCodes];
      updated[index].valid = false;
      setRowError(index, t('guestCodeDuplicate') || 'Dieser Code wurde bereits eingegeben.');
      onGuestCodesUpdate?.(updated);
      // Do not force focus/select here; allow user to focus any valid or empty field
      return;
    }
    if (code !== '') {
      try {
        const response = await apiFetch(`guests/${encodeURIComponent(code)}`);
        if (response.status === 200) {
          const data = await response.json();
          // Use guestCodesRef.current (not the stale filledGuestCodes closure) so that
          // seat assignments made by the auto-seat effect while this fetch was in flight
          // are not overwritten.
          const latestFilled = guestCodesRef.current.concat(
            Array(Math.max(0, maxGuestCodes - guestCodesRef.current.length)).fill({ guestCode: '', guestGuid: '', seatGuid: '', valid: null })
          );
          const updated = [...latestFilled];
          updated[index] = {
            guestCode: data.guestCode || data.code || data.guest_code || data.guestcode || data.name || code,
            guestGuid: data.guestGuid || data.guid || data.guest_guid || data.id || '',
            seatGuid: updated[index].seatGuid || '',
            valid: true,
          };
          const filtered = updated.filter(e => e.guestCode.trim() !== '' || e.guestGuid || e.valid);
          setRowError(index, null);
          onGuestCodesUpdate?.(filtered);
          // Do not force focus/select here; allow user to focus any valid or empty field
        } else {
          const latestFilled = guestCodesRef.current.concat(
            Array(Math.max(0, maxGuestCodes - guestCodesRef.current.length)).fill({ guestCode: '', guestGuid: '', seatGuid: '', valid: null })
          );
          const updated = [...latestFilled];
          updated[index] = { ...updated[index], valid: false };
          const filtered = updated.filter(e => e.guestCode.trim() !== '' || e.guestGuid || e.valid);
          setRowError(index,
            response.status === 404
              ? (t('guestCodeUnknown') || 'Unbekannter Buchungscode.')
              : (t('guestCodeValidationFailed') || 'Code konnte nicht validiert werden.')
          );
          onGuestCodesUpdate?.(filtered);
          // Do not force focus/select here
        }
      } catch (e) {
        const latestFilled = guestCodesRef.current.concat(
          Array(Math.max(0, maxGuestCodes - guestCodesRef.current.length)).fill({ guestCode: '', guestGuid: '', seatGuid: '', valid: null })
        );
        const updated = [...latestFilled];
        updated[index] = { ...updated[index], valid: false };
        const filtered = updated.filter(e => e.guestCode.trim() !== '' || e.guestGuid || e.valid);
        setRowError(index, t('guestCodeNetworkError') || 'Netzwerkfehler bei der Validierung.');
        onGuestCodesUpdate?.(filtered);
        // Do not force focus/select here
      }
    } // No else block needed for empty code: do not force focus/select
  };

  // Validate the first guest code if it is pre-filled and not yet validated
  // (Rollback: remove auto-validation effect)

  const handleInputFocus = (index: number) => {
    const entry = filledGuestCodes[index];
    if (entry && (entry.valid === true || !entry.guestCode)) {
      const input = inputRefs.current[index];
      if (input) {
        input.select();
      }
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    const total = filledGuestCodes.length;
    if (event.key === 'Enter') {
      event.preventDefault();
      handleInputBlur(index);
      // Move to next input if not last
      const nextInput = inputRefs.current[index + 1];
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    } else if (event.key === 'Tab') {
      handleInputBlur(index);
      if (event.shiftKey) {
        // Shift+Tab: move to previous input
        event.preventDefault();
        const prevInput = inputRefs.current[index - 1];
        if (prevInput) {
          prevInput.focus();
          prevInput.select();
        }
      } else {
        // Tab: move to next input
        event.preventDefault();
        const nextInput = inputRefs.current[index + 1];
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      const prevInput = inputRefs.current[index - 1];
      if (prevInput) {
        prevInput.focus();
        prevInput.select();
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      const nextInput = inputRefs.current[index + 1];
      if (nextInput) {
        nextInput.focus();
        nextInput.select();
      }
    }
  };

  // On confirm assignments, show final screen and perform implicit logout
  const handleFinalConfirm = () => {
    
    if (typeof onAssignSeat === 'function') {
      // Call the onAssignSeat callback with the current guest codes
      onAssignSeat(filledGuestCodes.filter(entry => entry.guestCode.trim() !== '' && entry.valid === true));
      setAssignmentsConfirmed(true);
    };
    // Clear the guest codes after confirmation  
  } 



  return (
    <div className="guest-list-container">
      <h2 className="guestlist-header">{t('guestList')}</h2>
      <table className="guest-list-table">
        <thead className="guestlist-tableheader">
          <tr>
            <th>{t('guestCode')}</th>
            <th>{t('status')}</th>
            <th>{t('assignedSeat')}</th>
          </tr>
        </thead>
        <tbody>
          {filledGuestCodes.map((entry, index) => (
            <tr key={index}>
              <td>
                {entry.locked ? (
                  // Locked initial members: render the display name (no booking
                  // code is exposed) and a lock icon. The user cannot edit
                  // these rows.
                  <span className="guest-code-locked" translate="no" title={t('lockedGuest') || 'Bestehende Reservierung'}>
                    🔒 {entry.displayName || ''}
                  </span>
                ) : (
                  <input
                    className={`guest-code-input${entry.valid === false ? ' invalid' : ''}`}
                    translate="no"
                    ref={el => { inputRefs.current[index] = el; }}
                    type="text"
                    value={entry.guestCode}
                    onChange={e => handleGuestCodeChange(index, e.target.value)}
                    onBlur={() => handleInputBlur(index)}
                    onFocus={() => handleInputFocus(index)}
                    onKeyDown={e => handleKeyDown(index, e)}
                    placeholder={`${t('guestCode')} ${index + 1}`}
                    maxLength={BOOKING_CODE_MAX_LENGTH}
                    inputMode="text"
                    autoCapitalize="characters"
                    autoComplete="off"
                    spellCheck={false}
                    aria-invalid={entry.valid === false}
                    aria-describedby={rowErrors[index] ? `guest-error-${index}` : undefined}
                  />
                )}
                {rowErrors[index] && (
                  <div id={`guest-error-${index}`} className="guest-code-error" role="alert">
                    {rowErrors[index]}
                  </div>
                )}
              </td>
              <td>
                {entry.locked ? (
                  <img src="./images/system/valid.png" alt="locked" className="status-icon valid" />
                ) : (
                  <>
                    {entry.valid === null && (
                      <img src="./images/system/invalid.png" alt="unknown" className="status-icon unknown" />
                    )}
                    {entry.valid === true && (
                      <img src="./images/system/valid.png" alt="Valid" className="status-icon valid" />
                    )}
                    {entry.valid === false && (
                      <img src="./images/system/invalid.png" alt="Invalid" className="status-icon invalid" />
                    )}
                  </>
                )}
              </td>
              <td>
                {(() => {
                  if (entry.seatGuid && entry.seatGuid.trim() !== '') {
                    const seatEntry = seatToGuestMap[entry.seatGuid];
                    if (seatEntry) {
                      // Check if seat is assigned to a different session
                      const isConflict = !!seatEntry.sessionId && seatEntry.sessionId !== sessionStorage.getItem('sessionId');
                      const isConfirmed = seatEntry.confirmed === true;
                      const stateClass = isConflict ? 'seat-conflict' : (isConfirmed ? 'seat-confirmed' : '');
                      return (
                        <span className={stateClass}>
                          {`${seatEntry.tableID || '-'}`}
                        </span>
                      );
                    }
                    return t('unassigned');
                  }
                })()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {extensionMode && (() => {
        // Show how many of the additional-guest slots are still available.
        const usedAdded = filledGuestCodes.filter(e => !e.locked && e.guestCode && e.guestCode.trim() !== '').length;
        const remaining = Math.max(0, maxAdditionalGuests - usedAdded);
        return (
          <div className="extension-slots-info" style={{ margin: '8px 0', fontSize: 14 }}>
            {t('slotsRemaining', { remaining, max: maxAdditionalGuests })
              || `Verbleibende Zusatzpl\u00e4tze: ${remaining}/${maxAdditionalGuests}`}
          </div>
        );
      })()}
      {canAssignSeat && (
        <div className="guestlist-status-info">
          {extensionMode ? (
            noTablesAvailable ? (
              <div className="guestlist-status-error" style={{ color: '#a64500', fontWeight: 'bold' }}>
                {t('extensionNoTablesAvailable')}
              </div>
            ) : tableChangeRequired ? (
              <ul className="guestlist-instructions">
                <li>{t('extensionPickNewTableInstruction')}</li>
              </ul>
            ) : (
              <ul className="guestlist-instructions">
                <li>{t('extensionAutoSeatedInstruction')}</li>
              </ul>
            )
          ) : (
            <ul className="guestlist-instructions">
              <li>{t('minGuestsEntered')}</li>
              <li>Wählen Sie jetzt den gewünschten Tisch aus. Das System markiert automatisch die gewünschte Anzahl an Plätzen. Vor Ort besteht am Tisch freie Platzwahl.</li>
              <li>Dann mit „Tischwahl bestätigen“ den Buchungsvorgang abschließen.</li>
              <li>Sie erhalten eine Buchungsbestätigung per E-Mail.</li>
            </ul>
          )}
        </div>
      )}
      {!canAssignSeat && (
        <div className="guestlist-status-error">
          {t('enterMinGuests', { minGuestCodes })}
        </div>
      )}
      <button
        className="login-button confirm-assignments-btn"
        style={{ marginTop: '16px' }}
        disabled={
          !canAssignSeat ||
          filledGuestCodes.filter((entry) => entry.guestCode.trim() !== '' && entry.valid !== true).length > 0 ||
          !filledGuestCodes.filter((entry) => entry.guestCode.trim() !== '' && entry.valid === true).every((entry) => entry.seatGuid)
        }
        onClick={handleFinalConfirm}
      >
        {extensionMode ? (t('confirmExtension') || 'Erweiterung best\u00e4tigen') : t('confirmAssignments')}
      </button>
      {assignmentsConfirmed && (
        <div className="confirmation-screen">
          <h2>{t('assignmentsConfirmedTitle') || 'Sitzplatzwahl bestätigt!'}</h2>
          <p>{t('assignmentsConfirmedText') || 'Ihre Sitzplatzwahl wurde erfolgreich gespeichert. Vielen Dank!'}</p>
        </div>
      )}
    </div>
  );
};

export default GuestList;
